import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const EmergenciesPage = () => {
  const { user, selectedBuilding } = useAuth();
  const [emergencies, setEmergencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panicDialogOpen, setPanicDialogOpen] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [formData, setFormData] = useState({
    building_id: '',
    description: '',
    location: ''
  });

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    fetchEmergencies();
  }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`);
      setBuildings(response.data);
      const defaultBuilding = (selectedBuilding && selectedBuilding !== 'all') 
        ? selectedBuilding 
        : (response.data.length > 0 ? response.data[0].id : '');
      setFormData(prev => ({ ...prev, building_id: defaultBuilding }));
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchEmergencies = async () => {
    setLoading(true);
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}` : '';
      const response = await axios.get(`${API}/emergencies${query}`);
      setEmergencies(response.data);
    } catch (error) {
      console.error('Error fetching emergencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePanicButton = async () => {
    if (!formData.description || !formData.location) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      await axios.post(`${API}/emergencies`, formData);
      toast.success('Emergencia reportada. Se ha notificado al administrador.');
      setPanicDialogOpen(false);
      setFormData({
        building_id: buildings[0]?.id || '',
        description: '',
        location: ''
      });
      fetchEmergencies();
    } catch (error) {
      toast.error('Error al reportar emergencia');
    }
  };

  const handleCloseEmergency = async (emergencyId) => {
    const resolution = prompt('Ingresa las notas de resolución:');
    if (!resolution) return;

    try {
      await axios.post(`${API}/emergencies/${emergencyId}/close`, {
        resolution_notes: resolution
      });
      toast.success('Emergencia cerrada exitosamente');
      fetchEmergencies();
    } catch (error) {
      toast.error('Error al cerrar emergencia');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const activeEmergencies = emergencies.filter(e => e.status === 'active');
  const closedEmergencies = emergencies.filter(e => e.status === 'closed');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary" data-testid="emergencies-page-title">Emergencias</h1>
            <p className="text-text-secondary mt-1">Sistema de respuesta rápida</p>
          </div>
          <AlertDialog open={panicDialogOpen} onOpenChange={setPanicDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button 
                className="bg-status-error hover:bg-status-error/90 text-white rounded-full shadow-lg"
                data-testid="panic-button"
              >
                <AlertTriangle className="h-5 w-5 mr-2" />
                Botón de Emergencia
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="panic-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-status-error">
                  <AlertTriangle className="h-6 w-6" />
                  Activar Emergencia
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Estás a punto de reportar una emergencia. Se notificará inmediatamente al administrador.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Conjunto</Label>
                  <Select value={formData.building_id} onValueChange={(value) => setFormData({...formData, building_id: value})}>
                    <SelectTrigger data-testid="emergency-building-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {buildings.map(building => (
                        <SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Descripción de la emergencia</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe qué está sucediendo..."
                    rows={3}
                    data-testid="emergency-description-input"
                  />
                </div>
                <div>
                  <Label>Ubicación exacta</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Ej: Torre B, Apartamento 302"
                    data-testid="emergency-location-input"
                  />
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handlePanicButton}
                  className="bg-status-error hover:bg-status-error/90"
                  data-testid="confirm-emergency-button"
                >
                  Confirmar Emergencia
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Active Emergencies */}
        {activeEmergencies.length > 0 && (
          <div>
            <h2 className="text-xl font-heading font-semibold text-status-error mb-4">Emergencias Activas</h2>
            <div className="grid gap-4">
              {activeEmergencies.map((emergency) => (
                <Card key={emergency.id} className="p-6 border-2 border-status-error" data-testid={`emergency-active-${emergency.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="h-5 w-5 text-status-error" />
                        <Badge className="bg-status-error text-white">ACTIVA</Badge>
                      </div>
                      <p className="text-text-primary mb-3 font-medium">{emergency.description}</p>
                      <div className="flex items-center gap-4 text-sm text-text-secondary">
                        <span>Ubicación: <span className="font-medium">{emergency.location}</span></span>
                        <span>Reportado por: <span className="font-medium">{emergency.user_name}</span></span>
                        <span>Hora: <span className="font-medium">{new Date(emergency.created_at).toLocaleString('es-ES')}</span></span>
                      </div>
                    </div>
                    {user?.role === 'admin' && (
                      <Button
                        onClick={() => handleCloseEmergency(emergency.id)}
                        variant="outline"
                        className="border-status-success text-status-success hover:bg-status-success hover:text-white"
                        data-testid={`close-emergency-${emergency.id}`}
                      >
                        Cerrar Emergencia
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Closed Emergencies */}
        <div>
          <h2 className="text-xl font-heading font-semibold text-primary mb-4">Historial de Emergencias</h2>
          <div className="grid gap-4">
            {closedEmergencies.length === 0 ? (
              <Card className="p-12 text-center">
                <AlertTriangle className="h-12 w-12 text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">No hay emergencias cerradas en el historial</p>
              </Card>
            ) : (
              closedEmergencies.map((emergency) => (
                <Card key={emergency.id} className="p-6" data-testid={`emergency-closed-${emergency.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <Badge className="bg-slate-100 text-slate-700">CERRADA</Badge>
                  </div>
                  <p className="text-text-primary mb-3">{emergency.description}</p>
                  <div className="grid grid-cols-2 gap-4 text-sm text-text-secondary">
                    <div>
                      <span>Ubicación: <span className="font-medium">{emergency.location}</span></span>
                    </div>
                    <div>
                      <span>Reportado por: <span className="font-medium">{emergency.user_name}</span></span>
                    </div>
                    <div>
                      <span>Creada: <span className="font-medium">{new Date(emergency.created_at).toLocaleDateString('es-ES')}</span></span>
                    </div>
                    <div>
                      <span>Cerrada: <span className="font-medium">{new Date(emergency.closed_at).toLocaleDateString('es-ES')}</span></span>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmergenciesPage;